import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import type { ProductDto } from "@gaole/shared";
import { api } from "../lib/api";
import { EASE } from "../lib/motion";
import { ProductCard } from "../components/ProductCard";
import { PageHeader } from "../components/storefront/PageHeader";
import { EmptyState, PageLoader } from "../components/ui";
import { CategoryFilterMobileControls, CategoryFilterSidebar } from "../components/catalog/CategoryFilter";
import type { CatalogCategoryFlat } from "../lib/catalogCategories";

export function Shop() {
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [sort, setSort] = useState("newest");

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api<{ categories: CatalogCategoryFlat[] }>("/catalog/categories"),
  });

  const { data: products, isLoading } = useQuery({
    queryKey: ["products", search, categoryId, sort],
    queryFn: () =>
      api<{ items: ProductDto[]; total: number }>(
        `/catalog/products?pageSize=60&sort=${sort}${search ? `&search=${encodeURIComponent(search)}` : ""}${categoryId ? `&categoryId=${categoryId}` : ""}`,
      ),
  });

  const searchField = (
    <input
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search products…"
      className="h-[42px] w-full min-w-0 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none transition-colors placeholder:text-white/30 focus:border-brand-pink/60"
    />
  );

  const sortField = (
    <select
      value={sort}
      onChange={(e) => setSort(e.target.value)}
      aria-label="Sort products"
      className="h-[42px] w-[6.25rem] max-w-[6.25rem] rounded-xl border border-white/10 bg-surface py-2 pl-2 pr-6 text-[11px] outline-none lg:w-auto lg:max-w-none lg:min-w-[5.75rem] lg:py-2.5 lg:pl-2.5 lg:pr-7 lg:text-xs"
    >
      <option value="newest">Newest</option>
      <option value="price_asc">Price ↑</option>
      <option value="price_desc">Price ↓</option>
      <option value="name">A–Z</option>
    </select>
  );

  return (
    <div className="min-w-0 space-y-4 px-4 pb-10 pt-4 lg:px-6">
      <PageHeader
        title="Shop"
        description="Browse the catalog — filter by category or search for what you need."
      />

      {/* ---------- mobile: sticky filter bar + chips ---------- */}
      <div className="sticky top-[52px] z-30 min-w-0 max-w-full py-2 lg:hidden">
        {categories?.categories ? (
          <CategoryFilterMobileControls
            categories={categories.categories}
            selectedId={categoryId}
            onSelect={setCategoryId}
            shopProductFilter
            search={searchField}
            sort={sortField}
          />
        ) : (
          <div className="flex min-w-0 gap-2">
            {searchField}
            {sortField}
          </div>
        )}
      </div>

      {/* ---------- desktop: sidebar filters + wide grid ---------- */}
      <div className="lg:flex lg:gap-8">
        <aside className="mb-4 hidden w-52 shrink-0 lg:block">
          <div className="sticky top-24 space-y-4">
            <div className="flex gap-2">{searchField}</div>
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40">Sort</p>
              <div className="flex">{sortField}</div>
            </div>
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-white/40">Categories</p>
              {categories?.categories ? (
                <CategoryFilterSidebar
                  categories={categories.categories}
                  selectedId={categoryId}
                  onSelect={setCategoryId}
                  shopProductFilter
                />
              ) : null}
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {products?.total != null && !isLoading && (
            <p className="mb-3 hidden text-[11px] text-white/40 lg:block">
              {products.total} item{products.total === 1 ? "" : "s"}
              {search ? ` matching “${search}”` : ""}
            </p>
          )}

          {isLoading ? (
            <PageLoader />
          ) : products?.items.length ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {products.items.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: (i % 5) * 0.04, duration: 0.38, ease: EASE.outExpo }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          ) : (
            <EmptyState title="No products match" hint="Try a different search or category." />
          )}
        </div>
      </div>
    </div>
  );
}
